<script setup lang="ts">
import { PDF_TEMPLATES } from '~/constants/cvTemplates'
import type { TemplateShot } from './TemplateLightbox.vue'

/**
 * Under the chat: what the interview turns into, so the visitor sees what
 * they get for finishing it. First the PDF layouts (Basic marked free, it
 * downloads without a watermark), then the same CV as a personal website,
 * which comes with the Job Search Pack. The pictures are the same page shots
 * the showcases use.
 *
 * The thumbnails are too small to read, so each opens large in
 * LandingTemplateLightbox, stepping within its own row.
 *
 * Phones scroll the PDF row sideways; wider screens fit all of it.
 */
const { t } = useI18n()

const pdfShots = computed<TemplateShot[]>(() => PDF_TEMPLATES.map(id => ({
  id,
  name: t(`cv.templates.${id}.name`),
  src: `/${id}.png`,
  width: 900,
  height: 1272,
})))

// Signature first: the design a new web CV gets (see BusinessWebTemplateSelector)
const webShots = computed<TemplateShot[]>(() => (['signature', 'sofax', 'classic', 'modern'] as const).map(id => ({
  id,
  name: t(`cv.businessWebTemplates.${id}.name`),
  src: `/web-${id}.png`,
  width: 1400,
  height: 1144,
})))

const lightbox = ref<{ open: (shots: TemplateShot[], index: number) => void } | null>(null)

function open(shots: TemplateShot[], index: number) {
  lightbox.value?.open(shots, index)
}
</script>

<template>
  <div class="mx-auto mt-8 max-w-5xl md:mt-10">
    <!-- The PDF layouts -->
    <p class="!mb-6 text-center !text-sm font-semibold text-ink dark:text-white">{{ t('cvChat.templates.title') }}</p>
    <ul class="template-row -mx-5 flex snap-x gap-3 overflow-x-auto px-5 pb-2 sm:-mx-6 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-8 lg:overflow-visible lg:px-0">
      <li v-for="(shot, index) in pdfShots" :key="shot.id" class="w-24 shrink-0 snap-start sm:w-28 lg:w-auto">
        <button
          type="button"
          class="group block w-full text-left focus:outline-none"
          :aria-label="t('cvChat.templates.open', { name: shot.name })"
          @click="open(pdfShots, index)"
        >
          <span class="thumb">
            <NuxtImg
              :src="shot.src"
              :alt="t('cvChat.templates.alt', { name: shot.name })"
              format="webp"
              quality="80"
              width="224"
              height="317"
              sizes="xs:112px lg:128px"
              loading="lazy"
              class="aspect-[1/1.414] w-full object-cover object-top"
            />
            <span
              v-if="shot.id === 'basic'"
              class="absolute left-1.5 top-1.5 rounded-full bg-accent-strong px-2 py-0.5 !text-[0.6875rem] font-bold !leading-tight !text-white"
            >
              {{ t('cvChat.templates.free') }}
            </span>
            <span
              v-else
              class="absolute left-1.5 top-1.5 rounded-full bg-lilac px-2 py-0.5 !text-[0.6875rem] font-bold !leading-tight !text-ink"
            >
              {{ t('cvChat.templates.premium') }}
            </span>
            <!-- Hover hint that the page opens large -->
            <span class="expand-hint" aria-hidden="true">
              <span class="inline-flex size-9 items-center justify-center rounded-full bg-white text-ink shadow-lift">
                <i class="bi bi-arrows-angle-expand" />
              </span>
            </span>
          </span>
          <span class="mt-1.5 block truncate text-center !text-xs text-muted dark:text-gray-400">{{ shot.name }}</span>
        </button>
      </li>
    </ul>

    <!-- …and the same CV as a website, with the pack -->
    <div class="mt-8 md:mt-10">
      <p class="!mb-2 flex flex-wrap items-center justify-center gap-2 text-center !text-sm font-semibold text-ink dark:text-white">
        {{ t('cvChat.templates.webTitle') }}
        <span class="rounded-full bg-lilac px-2 py-0.5 !text-[0.6875rem] font-bold !leading-tight text-ink">{{ t('cvChat.templates.premium') }}</span>
      </p>
      <p class="!mb-6 text-center !text-xs text-muted dark:text-gray-400">{{ t('cvChat.templates.webText') }}</p>
      <ul class="mx-auto grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">
        <li v-for="(shot, index) in webShots" :key="shot.id">
          <button
            type="button"
            class="group block w-full text-left focus:outline-none"
            :aria-label="t('cvChat.templates.openWeb', { name: shot.name })"
            @click="open(webShots, index)"
          >
            <!-- A browser frame, so it reads as a website at a glance -->
            <span class="thumb">
              <span class="flex items-center gap-1 border-b border-line bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-900" aria-hidden="true">
                <span class="size-1.5 rounded-full bg-red-400" />
                <span class="size-1.5 rounded-full bg-amber-400" />
                <span class="size-1.5 rounded-full bg-green-400" />
              </span>
              <NuxtImg
                :src="shot.src"
                :alt="t('cvChat.templates.webAlt', { name: shot.name })"
                format="webp"
                quality="80"
                width="480"
                height="392"
                sizes="xs:120px sm:240px"
                loading="lazy"
                class="aspect-[1.22/1] w-full object-cover object-top"
              />
              <span class="expand-hint" aria-hidden="true">
                <span class="inline-flex size-9 items-center justify-center rounded-full bg-white text-ink shadow-lift">
                  <i class="bi bi-arrows-angle-expand" />
                </span>
              </span>
            </span>
            <span class="mt-1.5 block truncate text-center !text-xs text-muted dark:text-gray-400">{{ shot.name }}</span>
          </button>
        </li>
      </ul>
    </div>

    <!-- The page large -->
    <LandingTemplateLightbox ref="lightbox" />
  </div>
</template>

<style scoped>
.template-row {
  scrollbar-width: none;
}

.template-row::-webkit-scrollbar {
  display: none;
}

.thumb {
  @apply relative block overflow-hidden rounded-md border border-line bg-white shadow-sm transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lift group-focus-visible:ring-2 group-focus-visible:ring-accent dark:border-gray-700;
}

.expand-hint {
  @apply absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition duration-200 group-hover:bg-ink/25 group-hover:opacity-100;
}
</style>
